/**
 * TanStack Router config.
 *
 * Rotas / login / health já existem. As rotas autenticadas usam
 * `AppLayout` envolvendo o conteúdo. Telas reais de cada rota
 * (stores, tvs, products, etc.) entram em subtasks de M1.E4.S2-S4 —
 * por ora são placeholders que renderizam dentro do layout.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from "@tanstack/react-router";

import { AppLayout } from "@/components/AppLayout";
import { HealthStatus } from "@/components/HealthStatus";
import { QaEnvironmentBanner } from "@/components/QaEnvironmentBanner";
import { RequireAuth } from "@/components/RequireAuth";
import { AreasPage } from "@/pages/AreasPage";
import { BrandingPage } from "@/pages/BrandingPage";
import { ClientsPage } from "@/pages/ClientsPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { ImportMappingEditPage } from "@/pages/ImportMappingEditPage";
import { ImportMappingsPage } from "@/pages/ImportMappingsPage";
import { ImportsPage } from "@/pages/ImportsPage";
import { ImportsHistoryPage } from "@/pages/imports/ImportsHistoryPage";
import { LoginPage, validateLoginSearch } from "@/pages/LoginPage";
import { ResetPasswordPage, validateResetPasswordSearch } from "@/pages/ResetPasswordPage";
import { PairTVPage } from "@/pages/PairTVPage";
import { SelectTenantPage } from "@/pages/SelectTenantPage";
import { ProductDetailPage } from "@/pages/ProductDetailPage";
import { ProductsPage } from "@/pages/ProductsPage";
import { StoreDetailPage } from "@/pages/StoreDetailPage";
import { StoresPage } from "@/pages/StoresPage";
import { TemplateEditPage } from "@/pages/TemplateEditPage";
import { TemplateGeneratePage } from "@/pages/TemplateGeneratePage";
import {
  TemplateDraftsPage,
  type TemplateDraftsSearch,
} from "@/pages/TemplateDraftsPage";
import { TemplatesPage } from "@/pages/TemplatesPage";
import { TVDetailPage } from "@/pages/TVDetailPage";
import { UsersPage } from "@/pages/UsersPage";
import { CatalogPage } from "@/pages/CatalogPage";
import { CatalogQueuePage } from "@/pages/CatalogQueuePage";
import { CatalogStagePage } from "@/pages/CatalogStagePage";
import { CatalogDetailPage } from "@/pages/CatalogDetailPage";
import { CampaignsPage } from "@/pages/CampaignsPage";
import { CampaignEditorPage } from "@/pages/CampaignEditorPage";
import {
  CampaignTemplatesGalleryPage,
  type CampaignTemplatesSearch,
} from "@/pages/CampaignTemplatesGalleryPage";
import {
  CustomArtsPage,
  type CustomArtsSearch,
} from "@/pages/CustomArtsPage";
import { MeusDadosPage } from "@/pages/MeusDadosPage";
import { ArtsReviewPage } from "@/pages/admin/ArtsReviewPage";
import { QueueStatusPage } from "@/pages/admin/QueueStatusPage";
import { SupplierArtsPage } from "@/pages/catalog/SupplierArtsPage";

const rootRoute = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  return (
    <div className="min-h-full">
      {/* Faixa de ambiente QA — todas as rotas, login incluído. */}
      <QaEnvironmentBanner />
      <Outlet />
    </div>
  );
}

function PageStub({ title }: { title: string }) {
  return (
    <RequireAuth>
      <AppLayout>
        <div className="mx-auto max-w-3xl">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            Implementação real entra nas subtasks seguintes da Story M1.E4.
          </p>
        </div>
      </AppLayout>
    </RequireAuth>
  );
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: DashboardPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: validateLoginSearch,
});

// /reset-password?token= — pública, destino do link de "Esqueci a senha" e
// do "Resetar senha" do admin (PSHWO-67053). Sem RequireAuth: quem chega aqui
// não está logado.
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
  validateSearch: validateResetPasswordSearch,
});

// /select-tenant — tela de seleção global para super_admin (TH-54656).
// O próprio componente delega para `RequireAuth` indiretamente (via
// useAuth) e bloqueia rotas autenticadas enquanto a seleção não existir.
const selectTenantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/select-tenant",
  component: SelectTenantPage,
});

const healthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/health",
  component: HealthPage,
});

function HealthPage() {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
      <h1 className="text-3xl font-semibold">Status da API</h1>
      <HealthStatus />
      <p className="text-xs text-[var(--color-muted-foreground)]">
        Atualiza a cada 30 segundos.
      </p>
    </main>
  );
}

const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/clients",
  component: ClientsPage,
});

// Placeholders das rotas referenciadas pela sidebar (TH-53153).
// Implementações reais entram em M1.E4.S2/S3/S4.
const storesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stores",
  component: StoresPage,
});
const storeDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stores/$storeId",
  component: StoreDetailPage,
});
const areasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/areas",
  component: AreasPage,
});
const tvsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tvs",
  component: () => <PageStub title="Telas" />,
});
const tvDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tvs/$tvId",
  component: TVDetailPage,
});
const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products",
  component: ProductsPage,
});
// Detalhe de produto — TH-53224 (M2.E8.S2.2). Substituiu o stub original.
const productDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products/$productId",
  component: ProductDetailPage,
});
// Tela /templates — TH-53259 (M3.E6.S1.4). Substituiu o PageStub.
const templatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/templates",
  component: TemplatesPage,
});
// Brief form /templates/generate — TH-55174 (M-IAtpl.5).
// Registrado ANTES de /templates/$templateId pra evitar match com
// "generate" como UUID inválido.
const templateGenerateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/templates/generate",
  component: TemplateGeneratePage,
});
// Galeria de drafts /templates/drafts — TH-55175 (M-IAtpl.6).
// Idem: ANTES de /templates/$templateId. Filtros (status/category/style)
// persistem na query string via validateSearch.
const VALID_STATUSES = new Set([
  "pending",
  "generating",
  "draft",
  "pending_review",
  "approved",
  "rejected",
  "failed",
]);
const VALID_CATEGORIES = new Set([
  "hortifruti",
  "acougue",
  "padaria",
  "mercearia",
  "bebidas",
  "promo",
  "limpeza",
  "sazonal",
]);
const VALID_STYLES = new Set(["minimalista", "complexo", "combo_a_b", "sazonal"]);

const templateDraftsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/templates/drafts",
  component: TemplateDraftsPage,
  validateSearch: (raw: Record<string, unknown>): TemplateDraftsSearch => ({
    status:
      typeof raw.status === "string" && VALID_STATUSES.has(raw.status)
        ? (raw.status as TemplateDraftsSearch["status"])
        : undefined,
    category:
      typeof raw.category === "string" && VALID_CATEGORIES.has(raw.category)
        ? (raw.category as TemplateDraftsSearch["category"])
        : undefined,
    style:
      typeof raw.style === "string" && VALID_STYLES.has(raw.style)
        ? (raw.style as TemplateDraftsSearch["style"])
        : undefined,
  }),
});
// Editor /templates/:id — TH-53258 (M3.E6.S1.3).
const templateEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/templates/$templateId",
  component: TemplateEditPage,
});
const importsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/imports",
  component: ImportsPage,
});
const importsHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/imports/history",
  component: ImportsHistoryPage,
});
const importMappingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/import-mappings",
  component: ImportMappingsPage,
});
const importMappingNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/import-mappings/new",
  component: ImportMappingEditPage,
});
const importMappingEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/import-mappings/$mappingId",
  component: ImportMappingEditPage,
});
const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/users",
  component: UsersPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: () => <PageStub title="Ajustes" />,
});

// /settings/branding — identidade visual do tenant (TH-55126).
const settingsBrandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/branding",
  component: BrandingPage,
});

// /meus-dados — perfil + troca de senha do usuário logado (TH-55255).
const meusDadosRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/meus-dados",
  component: MeusDadosPage,
});

// Painel super_admin /admin/queue — TH-53262 (M3.E7.S1.2).
const adminQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/queue",
  component: QueueStatusPage,
});

// Curadoria de artes /admin/arts/review — TH-55167 (M-artes3.8).
// Visível para super_admin e catalog_operator; role guard inline na page.
const adminArtsReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/arts/review",
  component: ArtsReviewPage,
});

// /pair — bootstrap pairing via QR scan (TH-54512).
// Query params chegam crus do QR; validamos como strings opcionais
// e o componente faz coerção (sdk vira number, fp recortado, etc).
interface PairSearch {
  ip?: string;
  port?: string;
  fp?: string;
  model?: string;
  mac?: string;
  sdk?: string;
}
const pairRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pair",
  component: PairTVPage,
  validateSearch: (raw: Record<string, unknown>): PairSearch => ({
    ip: typeof raw.ip === "string" ? raw.ip : undefined,
    port: typeof raw.port === "string" ? raw.port : undefined,
    fp: typeof raw.fp === "string" ? raw.fp : undefined,
    model: typeof raw.model === "string" ? raw.model : undefined,
    mac: typeof raw.mac === "string" ? raw.mac : undefined,
    sdk:
      typeof raw.sdk === "string"
        ? raw.sdk
        : typeof raw.sdk === "number"
          ? String(raw.sdk)
          : undefined,
  }),
});

// Catalog mestre — M5.E5 (TH-53357/53359/53362).
// `/catalog/queue` é declarado ANTES de `/catalog/$cpId` para que o
// TanStack Router match-resolve "queue" como path literal (mais
// específico) em vez de tentar parsear como UUID.
const catalogQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/catalog/queue",
  component: CatalogQueuePage,
});
// `/catalog/stage` antes do `$cpId` para que "stage" seja path literal.
const catalogStageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/catalog/stage",
  component: CatalogStagePage,
});
// `/catalog/suppliers/$supplierId/arts` — TH-55167 (M-artes3.8).
// DEVE ser declarada antes de catalogDetailRoute para que "suppliers"
// seja segmento literal e não interpretado como $cpId.
const catalogSupplierArtsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/catalog/suppliers/$supplierId/arts",
  component: SupplierArtsPage,
});
const catalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/catalog",
  component: CatalogPage,
});
const catalogDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/catalog/$cpId",
  component: CatalogDetailPage,
});

// Tela /custom-arts — TH-53368 (M5.E6.S2.1) + galeria com abas TH-55166.
// validateSearch persiste aba selecionada na query string (?tab=Minhas).
const customArtsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/custom-arts",
  component: CustomArtsPage,
  validateSearch: (raw: Record<string, unknown>): CustomArtsSearch => ({
    tab:
      raw.tab === "Minhas" ||
      raw.tab === "Priceshow" ||
      raw.tab === "Patrocinadas"
        ? raw.tab
        : undefined,
  }),
});

// Telas de campanhas — mock pós-MVP (fora dos marcos M0–M6).
// TODO: remover mock e conectar ao backend quando campanhas forem implementadas.
// `/campaigns` antes de `/campaigns/$campaignId` para o literal "new" não colidir.
const campaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/campaigns",
  component: CampaignsPage,
});

// Galeria de templates — TH-55142 (M-campT.4).
// DEVE ser declarada ANTES de campaignEditorRoute para que o segmento literal
// "templates" não seja interpretado como parâmetro $campaignId.
const campaignTemplatesGalleryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/campaigns/templates",
  component: CampaignTemplatesGalleryPage,
  validateSearch: (raw: Record<string, unknown>): CampaignTemplatesSearch => ({
    category: typeof raw.category === "string" ? raw.category : undefined,
    q: typeof raw.q === "string" ? raw.q : undefined,
  }),
});

const campaignEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/campaigns/$campaignId",
  component: CampaignEditorPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  resetPasswordRoute,
  selectTenantRoute,
  healthRoute,
  clientsRoute,
  storesRoute,
  storeDetailRoute,
  areasRoute,
  tvsRoute,
  tvDetailRoute,
  productsRoute,
  productDetailRoute,
  templatesRoute,
  templateGenerateRoute,
  templateDraftsRoute,
  templateEditRoute,
  importsRoute,
  importsHistoryRoute,
  importMappingsRoute,
  importMappingNewRoute,
  importMappingEditRoute,
  usersRoute,
  settingsRoute,
  settingsBrandingRoute,
  meusDadosRoute,
  adminQueueRoute,
  adminArtsReviewRoute,
  catalogRoute,
  catalogQueueRoute,
  catalogStageRoute,
  catalogSupplierArtsRoute,
  catalogDetailRoute,
  customArtsRoute,
  campaignsRoute,
  campaignTemplatesGalleryRoute,
  campaignEditorRoute,
  pairRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
